import { useEffect } from "react";
import { motion } from "framer-motion";
import { Mail, MessageSquare, Phone, Send, MousePointerClick, GitBranch, UserCheck, Ban } from "lucide-react";
import { RevShell, ModuleBar, RevHero, SectionHead, RevCTA, BrowserFrame, ComplianceNote } from "./_shared";

const CHANNELS = [
  { icon: Mail, t: "Email", note: "Personalized, verified sends" },
  { icon: MessageSquare, t: "SMS", note: "Only with documented consent" },
  { icon: Send, t: "WhatsApp", note: "Only with documented consent" },
  { icon: Phone, t: "Voice", note: "AI call for opted-in leads" },
  { icon: MousePointerClick, t: "Website chat", note: "In-session follow-ups" },
];

const SEQ = [
  { icon: Mail, t: "Personalized intro email", meta: "Day 0 · auto", tone: "brand" },
  { icon: Mail, t: "Follow-up email", meta: "Day 3 · if no reply", tone: "brand" },
  { icon: MessageSquare, t: "SMS nudge", meta: "Only if consent on file", tone: "amber" },
  { icon: Phone, t: "AI call", meta: "Qualified / opted-in only", tone: "violet" },
  { icon: UserCheck, t: "Human handoff", meta: "On any positive reply", tone: "emerald" },
  { icon: Ban, t: "Stop immediately", meta: "On opt-out or 'no'", tone: "rose" },
];

const SEQ_TONE = {
  brand: "text-brand bg-blue-50 border-blue-100",
  amber: "text-amber-600 bg-amber-50 border-amber-100",
  violet: "text-violet-600 bg-violet-50 border-violet-100",
  emerald: "text-emerald-600 bg-emerald-50 border-emerald-100",
  rose: "text-rose-600 bg-rose-50 border-rose-100",
};

function SequenceMock() {
  return (
    <BrowserFrame url="app.websitetech.pro/relay/sequence-builder">
      <div className="flex items-center gap-2 text-[11px] font-semibold text-slate-400 uppercase tracking-wide mb-3"><GitBranch size={13} /> Sequence · conditional branching</div>
      <div className="space-y-1.5">
        {SEQ.map((s, i) => (
          <div key={s.t}>
            <div className={`flex items-center gap-3 rounded-xl border px-3 py-2.5 ${SEQ_TONE[s.tone]}`}>
              <s.icon size={15} className="shrink-0" />
              <span className="text-sm font-semibold text-slate-800">{s.t}</span>
              <span className="ml-auto text-[10px] font-semibold opacity-80">{s.meta}</span>
            </div>
            {i < SEQ.length - 1 && <div className="ml-5 h-3 w-px bg-slate-200" />}
          </div>
        ))}
      </div>
    </BrowserFrame>
  );
}

export default function Outreach() {
  useEffect(() => { window.scrollTo(0, 0); document.title = "Relay — Outreach & Sequences | AI Revenue Center"; }, []);
  return (
    <RevShell testid="rev-outreach-page">
      <RevHero agent="Relay" eyebrow="AI Revenue Center™ · Outreach & Sequences"
        title="Follow up everywhere —" accent="stop the instant they say no."
        sub="A visual sequence builder across email, SMS, WhatsApp, voice and chat, with conditional branching. Every path is consent-first, and Relay hands off to a human on any positive reply."
        visual={<SequenceMock />} />
      <ModuleBar />

      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <SectionHead eyebrow="Channels" title="Meet people where they already are" sub="Each channel only fires when it's allowed — consent and frequency limits are enforced automatically." />
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 mt-8">
          {CHANNELS.map((c, i) => (
            <motion.div key={c.t} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.05 }}
              className="bg-white border border-slate-200 rounded-2xl card-shadow p-5 text-center">
              <div className="h-10 w-10 rounded-xl bg-brand/10 grid place-items-center text-brand mx-auto"><c.icon size={18} /></div>
              <div className="font-heading font-bold text-slate-900 mt-3">{c.t}</div>
              <p className="text-xs text-slate-500 mt-1">{c.note}</p>
            </motion.div>
          ))}
        </div>
      </section>

      <section className="py-16 bg-white border-y border-slate-100">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHead eyebrow="What we won't do" title="Growth without the risk" />
          <div className="grid sm:grid-cols-2 gap-3 mt-8">
            {["No aggressive LinkedIn automation — human-approved tasks only",
              "No cold SMS or WhatsApp without documented consent",
              "No unverified bulk prospect databases",
              "No fully autonomous launches without your approval"].map((x) => (
              <div key={x} className="flex items-start gap-2.5 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700"><Ban size={15} className="text-rose-500 mt-0.5 shrink-0" /> {x}</div>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <ComplianceNote>
          Every sequence honors opt-outs, suppression lists, calling-hour restrictions and per-channel consent. Relay stops the moment someone declines — and logs every send for your audit trail.
        </ComplianceNote>
      </section>

      <RevCTA title="Turn one reply into a booked meeting." sub="Start with a free audit and we'll map a compliant follow-up sequence for your business." />
    </RevShell>
  );
}
